import React, { useState, useEffect } from 'react';
import api from '../api';

const TIME_LABELS = { matin: 'Matin', midi: 'Midi', soir: 'Soir', libre: 'Libre' };
const SUGGESTIONS = [
  { name: 'Méditation', color: '#8b5cf6' },
  { name: 'Boire 2L d\'eau', color: '#3b82f6' },
  { name: 'Lecture 20min', color: '#f59e0b' },
  { name: 'Pas de téléphone au réveil', color: '#ef4444' },
  { name: 'Marche 30min', color: '#10b981' },
  { name: 'Étirements', color: '#ec4899' },
  { name: 'Journaling', color: '#6366f1' },
  { name: 'Vitamines', color: '#14b8a6' },
];

function getLast14Days() {
  const days = [];
  for (let i = 13; i >= 0; i--) {
    const d = new Date();
    d.setDate(d.getDate() - i);
    days.push(d.toISOString().split('T')[0]);
  }
  return days;
}

export default function HabitsPage() {
  const [habits, setHabits] = useState([]);
  const [logs, setLogs] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState({ name: '', color: '#6366f1', time_of_day: 'libre' });
  const days = getLast14Days();

  const load = async () => {
    try {
      const r = await api.get('/habits');
      setHabits(r.data.habits);
      setLogs(r.data.logs);
    } catch {}
  };

  useEffect(() => { load(); }, []);

  const addHabit = async (e) => {
    e.preventDefault();
    await api.post('/habits', form);
    setShowModal(false);
    setForm({ name: '', color: '#6366f1', time_of_day: 'libre' });
    load();
  };

  const addSuggestion = async (s) => {
    await api.post('/habits', { name: s.name, color: s.color, time_of_day: 'libre' });
    load();
  };

  const toggle = async (habitId, date) => {
    await api.post(`/habits/${habitId}/toggle`, { date });
    load();
  };

  const deleteHabit = async (id) => {
    await api.delete(`/habits/${id}`);
    load();
  };

  const isCompleted = (habitId, date) => logs.some(l => l.habit_id === habitId && l.log_date === date);

  const getStreak = (habitId) => {
    let streak = 0;
    for (let i = days.length - 1; i >= 0; i--) {
      if (isCompleted(habitId, days[i])) streak++;
      else break;
    }
    return streak;
  };

  const todayTotal = habits.length;
  const todayDone = habits.filter(h => isCompleted(h.id, days[days.length - 1])).length;

  return (
    <div>
      <div className="page-header page-header-row">
        <div>
          <h1>Habitudes quotidiennes</h1>
          <p>Aujourd'hui : {todayDone}/{todayTotal} habitudes faites</p>
        </div>
        <button className="btn btn-primary" onClick={() => setShowModal(true)}>+ Nouvelle habitude</button>
      </div>

      {habits.length === 0 ? (
        <div>
          <div className="empty-state" style={{ marginBottom: 24 }}>
            <div className="empty-emoji">&#127775;</div>
            <p>Commencez à suivre vos habitudes quotidiennes !</p>
          </div>
          <div className="card">
            <h3 style={{ fontSize: '0.9rem', marginBottom: 12 }}>Suggestions populaires</h3>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
              {SUGGESTIONS.map(s => (
                <button key={s.name} className="btn btn-secondary btn-sm" onClick={() => addSuggestion(s)} style={{ borderLeft: `3px solid ${s.color}` }}>
                  + {s.name}
                </button>
              ))}
            </div>
          </div>
        </div>
      ) : (
        <div className="habits-grid">
          {habits.map(habit => (
            <div key={habit.id} className="habit-row">
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 140 }}>
                <div style={{ width: 12, height: 12, borderRadius: '50%', background: habit.color, flexShrink: 0 }} />
                <div>
                  <div className="habit-name">{habit.name}</div>
                  <div className="habit-time-badge">{TIME_LABELS[habit.time_of_day]}</div>
                </div>
              </div>
              <div className="habit-days">
                {days.map(day => (
                  <button
                    key={day}
                    className={`habit-day ${isCompleted(habit.id, day) ? 'filled' : 'empty'}`}
                    style={isCompleted(habit.id, day) ? { background: habit.color } : {}}
                    onClick={() => toggle(habit.id, day)}
                    title={new Date(day + 'T00:00').toLocaleDateString('fr-FR', { weekday: 'short', day: 'numeric', month: 'short' })}
                  >
                    {new Date(day + 'T00:00').getDate()}
                  </button>
                ))}
              </div>
              <div className="habit-streak">{getStreak(habit.id)}j &#128293;</div>
              <button className="btn-ghost" onClick={() => deleteHabit(habit.id)}>&#128465;</button>
            </div>
          ))}
        </div>
      )}

      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <h2>Nouvelle habitude</h2>
            <form onSubmit={addHabit}>
              <div className="form-group">
                <label>Nom</label>
                <input className="form-input" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="Ex: Méditation, Sport, Lecture..." required />
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Moment de la journée</label>
                  <select className="form-input" value={form.time_of_day} onChange={e => setForm({ ...form, time_of_day: e.target.value })}>
                    <option value="matin">Matin</option>
                    <option value="midi">Midi</option>
                    <option value="soir">Soir</option>
                    <option value="libre">Libre</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Couleur</label>
                  <input type="color" value={form.color} onChange={e => setForm({ ...form, color: e.target.value })} style={{ width: 50, height: 38, border: 'none', cursor: 'pointer' }} />
                </div>
              </div>
              <div className="modal-actions">
                <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)}>Annuler</button>
                <button type="submit" className="btn btn-primary">Créer</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
